'use client';

import { X, Sparkles, TicketPercent, WalletCards } from 'lucide-react';
import type { ClaimedBenefitItem } from '../../../lib/api/wallet';

interface VoucherGrantModalProps {
  open: boolean;
  onClose: () => void;
  onGoToAccount: () => void;
  benefits: ClaimedBenefitItem[];
}

/** 分 → 元，保留两位小数并加千分位 */
function formatFen(v: number): string {
  const yuan = v / 100;
  return yuan.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function VoucherGrantModal({ open, onClose, onGoToAccount, benefits }: VoucherGrantModalProps) {
  if (!open) return null;

  const hasMultiple = benefits.length > 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <button aria-label="关闭权益弹窗遮罩" className="absolute inset-0 liquid-glass-overlay" onClick={onClose} type="button" />
      <div className="safeclaw-voucher-modal relative w-full max-w-sm overflow-hidden rounded-[28px] border border-white/40 bg-card p-5 text-center shadow-2xl dark:border-white/10">
        <button
          aria-label="关闭权益弹窗"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-secondary/70 text-muted-foreground transition-colors hover:text-foreground"
          onClick={onClose}
          type="button"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="safeclaw-voucher-burst" />
        <div className="relative mx-auto mt-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/25">
          <TicketPercent className="h-8 w-8" />
          <Sparkles className="absolute -right-1 -top-1 h-5 w-5 text-amber-200" />
        </div>

        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-300">领取成功</p>

        {hasMultiple ? (
          <h2 className="mt-2 text-xl font-bold tracking-normal text-foreground">已领取 {benefits.length} 项权益</h2>
        ) : (
          <h2 className="mt-2 text-3xl font-bold tracking-normal text-foreground">{benefits[0]?.name ?? '权益'}</h2>
        )}

        <p className="mx-auto mt-2 max-w-[260px] text-sm leading-6 text-muted-foreground">已发放到账户赠送余额，可在账户中心查看余额、账单与充值记录。</p>

        <div className="mt-5 space-y-2.5">
          {benefits.map((item) => (
            <div className="rounded-2xl border border-border/50 bg-secondary/40 p-3 text-left" key={`${item.triggerKey}-${item.rewardType}-${item.amount}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{item.name}</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-300">¥{formatFen(item.amount)}</span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background">
                <div className="safeclaw-voucher-progress h-full rounded-full bg-emerald-500" />
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground/70">有效期 {item.expireDays} 天</p>
            </div>
          ))}
        </div>

        <button
          className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-foreground text-sm font-semibold text-background transition-colors hover:bg-foreground/90"
          onClick={() => {
            onClose();
            onGoToAccount();
          }}
          type="button"
        >
          <WalletCards className="h-4 w-4" />
          查看账户中心
        </button>
      </div>
    </div>
  );
}
